import { useEffect, useState } from "react";
import axios from "axios";
import { Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "@/App";

const STALE_AFTER_SECONDS = 3600;
const POLL_INTERVAL_MS = 30_000;
// Forecast changes slowly (rolling 24h/7d averages + cycle math) — poll
// half as often as the live health endpoint to keep DB load down.
const FORECAST_POLL_INTERVAL_MS = 60_000;

const API_LABELS = {
  pnr_lookup: "PNR Lookup",
  train_status: "Train Status",
};

const formatIst = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }) + " IST";
  } catch {
    return null;
  }
};

const statusFor = (stats) => {
  if (!stats || !stats.last_call_at) {
    return { label: "Never called", tone: "text-zinc-400", dot: "bg-zinc-500" };
  }
  if (stats.last_call_success === false) {
    return {
      label: `Down${stats.last_error_type ? ` (${stats.last_error_type})` : ""}`,
      tone: "text-rose-400",
      dot: "bg-rose-500",
    };
  }
  const ageSec = (Date.now() - new Date(stats.last_call_at).getTime()) / 1000;
  if (ageSec > STALE_AFTER_SECONDS) {
    return { label: "Stale", tone: "text-amber-400", dot: "bg-amber-500" };
  }
  return { label: "Healthy", tone: "text-emerald-400", dot: "bg-emerald-500" };
};

// Human-readable reset window — matches the Telegram /apistatus formatting:
//   >7d → "Xd Yh", 1h..7d → "Xh Ym", <1h → "Xm".
const formatResetSeconds = (s) => {
  if (s == null) return null;
  if (s <= 0) return "now";
  const D = 86400, H = 3600, M = 60;
  if (s > 7 * D) {
    return `${Math.floor(s / D)}d ${Math.floor((s % D) / H)}h`;
  }
  if (s >= H) {
    return `${Math.floor(s / H)}h ${Math.floor((s % H) / M)}m`;
  }
  return `${Math.floor(s / M)}m`;
};

// Live seconds-to-reset from the stored reset_at instant (so a card open for
// minutes shows accurate remaining time without re-polling).
const secondsToReset = (resetAtIso) => {
  if (!resetAtIso) return null;
  const ms = new Date(resetAtIso).getTime();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.floor((ms - Date.now()) / 1000));
};

// Color thresholds per spec: <70 green, 70..89.999 yellow, >=90 red.
const quotaBarColor = (usedPct) => {
  if (usedPct == null) return "bg-zinc-600";
  if (usedPct >= 90) return "bg-rose-500";
  if (usedPct >= 70) return "bg-amber-500";
  return "bg-emerald-500";
};

// Renamed to ApiHealthCardLegacy as part of the Pro Dashboard upgrade
// (Phase 1 step 10). The new compact System Health card lives in
// ApiHealthCard.jsx. This file is retained for testid resolution and
// reachable via the "More details" disclosure in the new card.
export const ApiHealthCardLegacy = ({ user }) => {
  const [health, setHealth] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;
    const fetchHealth = async () => {
      try {
        const res = await axios.get(`${API}/health/apis?user_id=${user.id}`);
        if (cancelled) return;
        setHealth(res.data);
        setError(null);
      } catch (e) {
        if (cancelled) return;
        setError("Could not load API health");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHealth();
    const id = setInterval(fetchHealth, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.id]);

  // Separate poll for forecast — slower cadence, doesn't block the existing
  // health rendering on first paint, and forecast failures don't surface
  // errors (the section just doesn't render).
  useEffect(() => {
    if (!user?.id) return undefined;

    let cancelled = false;
    const fetchForecast = async () => {
      try {
        const res = await axios.get(`${API}/health/apis/forecast?user_id=${user.id}`);
        if (cancelled) return;
        setForecast(res.data);
      } catch (e) {
        if (cancelled) return;
        // Soft failure — keep prior forecast (if any).
      }
    };

    fetchForecast();
    const id = setInterval(fetchForecast, FORECAST_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [user?.id]);

  return (
    <Card className="bg-zinc-900/40 border-zinc-800/50 mb-8" data-testid="api-health-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-zinc-50 text-base">
          <Activity className="w-4 h-4 text-indigo-400" />
          External API Health
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Skeleton className="h-24 bg-zinc-800/40" />
            <Skeleton className="h-24 bg-zinc-800/40" />
          </div>
        ) : error ? (
          <p className="text-sm text-zinc-400">{error}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.keys(API_LABELS).map((name) => {
              const stats = health?.[name];
              const status = statusFor(stats);
              const lastIst = formatIst(stats?.last_call_at);
              const monthPct = stats && stats.month_count > 0
                ? ((stats.month_success_count / stats.month_count) * 100).toFixed(1)
                : null;
              const hasQuota = stats?.quota_limit != null && stats?.quota_remaining != null;
              const usedPct = hasQuota ? stats.quota_used_pct : null;
              // Clamp the visible bar to [0, 100] so a transient negative or
              // over-100 doesn't blow out the layout.
              const usedPctBar = Math.max(0, Math.min(100, usedPct ?? 0));
              const resetStr = hasQuota
                ? formatResetSeconds(secondsToReset(stats.quota_reset_at_utc))
                : null;
              return (
                <div
                  key={name}
                  className="rounded-lg border border-zinc-800/60 bg-zinc-900/40 p-4"
                  data-testid={`api-health-${name}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-zinc-200">{API_LABELS[name]}</span>
                    <span className={`flex items-center gap-2 text-xs ${status.tone}`}>
                      <span className={`inline-block w-2 h-2 rounded-full ${status.dot}`} />
                      {status.label}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-400 space-y-0.5">
                    {lastIst && stats?.last_call_success !== null ? (
                      <div>
                        Last call: {lastIst}
                        {stats.last_call_duration_ms != null && ` (${stats.last_call_duration_ms} ms)`}
                      </div>
                    ) : (
                      <div>Last call: —</div>
                    )}
                    <div>
                      Today: {stats?.today_success_count ?? 0}/{stats?.today_count ?? 0} successful
                    </div>
                    <div>
                      This month: {(stats?.month_success_count ?? 0).toLocaleString()}/
                      {(stats?.month_count ?? 0).toLocaleString()}
                      {monthPct ? ` (${monthPct}%)` : ""}
                    </div>
                  </div>

                  {hasQuota ? (
                    <div className="mt-3" data-testid={`api-health-quota-${name}`}>
                      <div className="flex items-center justify-between text-xs text-zinc-300 mb-1">
                        <span>
                          Quota: {stats.quota_remaining.toLocaleString()} /{" "}
                          {stats.quota_limit.toLocaleString()} remaining
                        </span>
                        {usedPct != null && (
                          <span className="text-zinc-400">{usedPct}% used</span>
                        )}
                      </div>
                      <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
                        <div
                          className={`h-full ${quotaBarColor(usedPct)} transition-[width] duration-500`}
                          style={{ width: `${usedPctBar}%` }}
                        />
                      </div>
                      {resetStr && (
                        <div className="text-xs text-zinc-500 mt-1">Resets in {resetStr}</div>
                      )}
                    </div>
                  ) : null}

                  {(() => {
                    const fc = forecast?.[name];
                    if (!fc) return null;
                    if (fc.confidence === "low") {
                      return (
                        <div
                          className="mt-3 text-xs text-zinc-500 italic"
                          data-testid={`api-health-forecast-${name}`}
                        >
                          Insufficient data for forecast — collecting metrics...
                        </div>
                      );
                    }
                    if (fc.projected_total_at_reset == null || fc.current_limit == null) {
                      return null;
                    }
                    const projPct = fc.projected_cycle_pct ?? 0;
                    const projPctBar = Math.max(0, Math.min(100, projPct));
                    const willExceed = !!fc.projected_will_exceed;
                    return (
                      <div className="mt-3" data-testid={`api-health-forecast-${name}`}>
                        <div className="flex items-center justify-between text-xs text-zinc-300 mb-1">
                          <span>
                            Projected at reset:{" "}
                            {fc.projected_total_at_reset.toLocaleString()} /{" "}
                            {fc.current_limit.toLocaleString()}
                          </span>
                          <span className={willExceed ? "text-rose-400" : "text-zinc-400"}>
                            {projPct}%
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-zinc-800 overflow-hidden">
                          <div
                            className={`h-full ${willExceed ? "bg-rose-500" : "bg-sky-500"} transition-[width] duration-500`}
                            style={{ width: `${projPctBar}%` }}
                          />
                        </div>
                        {willExceed && fc.warning ? (
                          <div className="text-xs text-rose-400 mt-1 flex items-center gap-1">
                            <span>⚠️</span>
                            <span>{fc.warning}</span>
                          </div>
                        ) : fc.warning ? (
                          <div className="text-xs text-amber-400 mt-1">{fc.warning}</div>
                        ) : null}
                        <div className="text-xs text-zinc-500 mt-1">
                          Forecast confidence: {fc.confidence}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
